import { useToast } from '@retracedgmbh/ui-components-lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@/hooks/useApi';
import type { UploadFileData } from '@/hooks/useFileUploadNew';
import * as api from '@/services/api';
import type {
  ClassifyResultsResponse,
  FileClassificationType,
} from '@/services/api/types/ai-processing';

const STORAGE_KEY = 'dropbox-bulk-upload:v1';

interface PersistedState {
  uploadedFiles: UploadFileData[];
  pollingFileIds: string[];
  typeOverrides: Record<string, FileClassificationType>;
}

function saveState(state: PersistedState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function loadState(): PersistedState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedState;
  } catch {
    return null;
  }
}

function clearState() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

function isTerminalStatus(status: string) {
  return status === 'completed' || status === 'failed';
}

export function useBulkFileUpload() {
  const persisted = useRef(loadState()).current;
  const [batchKey, setBatchKey] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadFileData[]>(
    persisted?.uploadedFiles ?? [],
  );
  const [pollingFileIds, setPollingFileIds] = useState<string[]>(
    persisted?.pollingFileIds ?? [],
  );
  const [typeOverrides, setTypeOverrides] = useState<
    Record<string, FileClassificationType>
  >(persisted?.typeOverrides ?? {});
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const classifyMutation = useMutation({
    mutationFn: (fileIds: string[]) =>
      api.aiProcessing.classifyDropBoxFiles(fileIds),
  });

  const acceptMutation = useMutation({
    mutationFn: (files: { fileId: string; acceptedType: string }[]) =>
      api.aiProcessing.acceptDropBoxFiles(files),
  });

  const isPolling = pollingFileIds.length > 0;
  const queryFileIds = isPolling
    ? pollingFileIds
    : uploadedFiles.filter(f => f.id).map(f => f.id as string);
  const hasFilesToQuery = queryFileIds.length > 0;

  const { data: classifyResults } = useQuery<ClassifyResultsResponse>({
    queryKey: ['classify-results', batchKey, queryFileIds],
    queryFn: async () => {
      const res = await api.aiProcessing.getClassifyResults(queryFileIds);
      return res.data.data;
    },
    enabled: hasFilesToQuery,
    refetchInterval: isPolling ? 5_000 : false,
  });

  const allDone = classifyResults?.results.length
    ? classifyResults.results.every(r => isTerminalStatus(r.status))
    : false;

  useEffect(() => {
    if (
      uploadedFiles.length === 0 &&
      pollingFileIds.length === 0 &&
      Object.keys(typeOverrides).length === 0
    ) {
      clearState();
      return;
    }
    saveState({ uploadedFiles, pollingFileIds, typeOverrides });
  }, [uploadedFiles, pollingFileIds, typeOverrides]);

  useEffect(() => {
    if (!isPolling || !allDone) return;

    setPollingFileIds([]);
    queryClient.invalidateQueries({ queryKey: ['ai-processing-summary'] });
    queryClient.invalidateQueries({ queryKey: ['ai-processing-stats'] });
  }, [isPolling, allDone, queryClient]);

  // Resume polling after restore if classification is still in progress
  useEffect(() => {
    if (isPolling || !classifyResults?.results.length) return;
    const hasInProgress = classifyResults.results.some(
      r => !isTerminalStatus(r.status),
    );
    if (hasInProgress) {
      setPollingFileIds(queryFileIds);
    }
  }, [classifyResults, isPolling, queryFileIds]);

  const handleUploadFinish = useCallback(
    async (files: UploadFileData[]) => {
      const validFiles = files.filter(f => f.id);
      if (validFiles.length === 0) return;

      setUploadedFiles(validFiles);

      const fileIds = validFiles.map(f => f.id as string);
      try {
        await classifyMutation.mutateAsync(fileIds);
        toast({ title: 'Files submitted for processing', variant: 'success' });
        setPollingFileIds(fileIds);
      } catch (error) {
        toast({
          title: 'Failed to submit files',
          description: (error as Error).message,
          variant: 'destructive',
        });
      }
    },
    [classifyMutation, toast],
  );

  const handleTypeChange = useCallback(
    (fileId: string, type: FileClassificationType) => {
      setTypeOverrides(prev => ({ ...prev, [fileId]: type }));
    },
    [],
  );

  const handleAccept = useCallback(async () => {
    const files = uploadedFiles
      .filter(f => f.id)
      .map(f => {
        const fileId = f.id as string;
        const result = classifyResults?.results.find(r => r.fileId === fileId);
        const acceptedType =
          typeOverrides[fileId] ?? result?.classification?.type ?? 'OTHER';
        return { fileId, acceptedType };
      });

    try {
      await acceptMutation.mutateAsync(files);
      toast({ title: 'Processing started', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['ai-processing-summary'] });
      queryClient.invalidateQueries({ queryKey: ['ai-processing-stats'] });
      resetBatch();
      return true;
    } catch (error) {
      toast({
        title: 'Failed to start processing',
        description: (error as Error).message,
        variant: 'destructive',
      });
      return false;
    }
  }, [
    uploadedFiles,
    classifyResults,
    typeOverrides,
    acceptMutation,
    toast,
    queryClient,
  ]);

  const resetBatch = useCallback(() => {
    setUploadedFiles([]);
    setPollingFileIds([]);
    setTypeOverrides({});
    setBatchKey(prev => prev + 1);
    clearState();
    queryClient.removeQueries({ queryKey: ['classify-results'] });
  }, [queryClient]);

  return {
    batchKey,
    uploadedFiles,
    handleUploadFinish,
    isClassifying: classifyMutation.isPending,
    isPolling,
    classifyResults,
    typeOverrides,
    handleTypeChange,
    handleAccept,
    isAccepting: acceptMutation.isPending,
    resetBatch,
  };
}
